import type { WidgetType } from '@flexbyt/shared';
import { cx } from '@flexbyt/widget-preview';
import type { ReactNode } from 'react';

/**
 * A layout, drawn small — for choosing one.
 *
 * The product's `WidgetThumbnail` is a wireframe: grey outlined boxes on grey,
 * right for the dashboard's dense chooser and wrong for a marketing page, where
 * eight of them in a row read as a form rather than as eight things worth
 * trying. This is the same set of arrangements drawn the way the widgets
 * actually look — white cards lifted off a tinted page, gold stars, a face on
 * every review — with no outlines anywhere, so the chosen one can say so with
 * colour instead of a border.
 *
 * Decoration: every caller labels the layout in words beside it.
 */
export function LayoutSketch({
  type,
  active = false,
  className,
}: {
  type: WidgetType;
  active?: boolean;
  className?: string;
}) {
  return (
    <div
      aria-hidden
      className={cx(
        'relative overflow-hidden rounded-xl p-2.5 transition-[background] duration-300',
        active
          ? 'bg-[linear-gradient(135deg,#e6edff_0%,#efe9ff_100%)]'
          : 'bg-[linear-gradient(135deg,#f3f4f7_0%,#f6f5f9_100%)]',
        className
      )}
    >
      {SKETCHES[type] ?? SKETCHES.grid}
    </div>
  );
}

// --- the parts --------------------------------------------------------------

const FACES = ['#f2c7a5', '#c9d7ff', '#f7d9e3', '#cdebd9', '#ffe2b3', '#e0d4ff'];

function Stars({ small = false }: { small?: boolean }) {
  return (
    <span className="flex gap-[2px]">
      {[0, 1, 2, 3, 4].map((index) => (
        <span
          key={index}
          className={cx('rounded-full bg-[#e0a93b]', small ? 'size-[3px]' : 'size-1')}
        />
      ))}
    </span>
  );
}

function Line({ width = 'w-full' }: { width?: string }) {
  return <span className={cx('block h-[3px] rounded-full bg-ink-200/80', width)} />;
}

function Face({ index, size = 'size-2' }: { index: number; size?: string }) {
  return (
    <span
      className={cx('block shrink-0 rounded-full', size)}
      style={{ backgroundColor: FACES[index % FACES.length] }}
    />
  );
}

/** One review card: stars, a few lines of words, the person who wrote them. */
function Card({
  lines = 2,
  face = 0,
  className,
}: {
  lines?: number;
  face?: number;
  className?: string;
}) {
  return (
    <span
      className={cx(
        'flex flex-col gap-[3px] rounded-[5px] bg-white p-1.5 shadow-[0_1px_2px_rgba(10,10,11,0.06),0_4px_10px_-6px_rgba(20,40,120,0.25)]',
        className
      )}
    >
      <Stars small />
      {Array.from({ length: lines }).map((_, line) => (
        <Line key={line} width={line === lines - 1 && lines > 1 ? 'w-2/3' : 'w-full'} />
      ))}
      <span className="mt-[2px] flex items-center gap-1">
        <Face index={face} size="size-[7px]" />
        <Line width="w-5" />
      </span>
    </span>
  );
}

/** A faded page, for the layouts that sit on top of one. */
function Page() {
  return (
    <span className="flex h-full flex-col gap-1.5 opacity-60">
      <span className="block h-1.5 w-10 rounded-full bg-ink-300/70" />
      <Line />
      <Line width="w-4/5" />
      <span className="mt-0.5 block flex-1 rounded-[4px] bg-white/70" />
    </span>
  );
}

// --- the layouts ------------------------------------------------------------

const SKETCHES: Partial<Record<WidgetType, ReactNode>> = {
  marquee: (
    <span className="flex h-full items-center gap-1.5 [mask-image:linear-gradient(90deg,transparent,black_14%,black_86%,transparent)]">
      {[0, 1, 2, 3, 4].map((index) => (
        <Card key={index} face={index} className="w-[34%] shrink-0" />
      ))}
    </span>
  ),

  slider: (
    <span className="flex h-full flex-col justify-center gap-1.5">
      <span className="flex items-center gap-1">
        <span className="size-2 shrink-0 rounded-full bg-white shadow-xs" />
        {[0, 1, 2].map((index) => (
          <Card key={index} face={index + 1} className="flex-1" />
        ))}
        <span className="size-2 shrink-0 rounded-full bg-white shadow-xs" />
      </span>
      <span className="flex justify-center gap-[3px]">
        <span className="h-[3px] w-2 rounded-full bg-ink-400" />
        <span className="size-[3px] rounded-full bg-ink-300" />
        <span className="size-[3px] rounded-full bg-ink-300" />
      </span>
    </span>
  ),

  masonry: (
    <span className="flex h-full gap-1.5">
      {[
        [3, 1],
        [1, 2, 1],
        [2, 3],
      ].map((column, index) => (
        <span key={index} className="flex flex-1 flex-col gap-1.5">
          {column.map((lines, cardIndex) => (
            <Card key={cardIndex} lines={lines} face={index * 2 + cardIndex} />
          ))}
        </span>
      ))}
    </span>
  ),

  grid: (
    <span className="grid h-full grid-cols-3 grid-rows-2 gap-1.5">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <Card key={index} lines={1} face={index} />
      ))}
    </span>
  ),

  list: (
    <span className="flex h-full flex-col justify-center gap-1.5">
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          className="flex items-center gap-1.5 rounded-[5px] bg-white p-1.5 shadow-[0_1px_2px_rgba(10,10,11,0.06),0_4px_10px_-6px_rgba(20,40,120,0.25)]"
        >
          <Face index={index + 2} size="size-3" />
          <span className="flex flex-1 flex-col gap-[3px]">
            <Stars small />
            <Line />
          </span>
        </span>
      ))}
    </span>
  ),

  quote: (
    <span className="flex h-full flex-col items-center justify-center gap-1 text-center">
      <span className="font-serif text-[22px] leading-[0.6] text-brand/70">&ldquo;</span>
      <Stars />
      <span className="mt-0.5 block h-1 w-4/5 rounded-full bg-ink-300/80" />
      <span className="block h-1 w-3/5 rounded-full bg-ink-300/80" />
      <span className="mt-1 flex items-center gap-1">
        <Face index={3} size="size-2.5" />
        <Line width="w-6" />
      </span>
    </span>
  ),

  popup: (
    <span className="relative block h-full">
      <Page />
      <span className="absolute inset-y-0 right-3 flex w-[42%] flex-col gap-1 rounded-[5px] bg-white p-1.5 shadow-[0_2px_4px_rgba(10,10,11,0.06),0_10px_20px_-8px_rgba(20,40,120,0.35)]">
        {[0, 1].map((index) => (
          <span key={index} className="flex flex-col gap-[3px]">
            <Stars small />
            <Line />
            <Line width="w-2/3" />
          </span>
        ))}
      </span>
      <span className="absolute top-1/2 right-0 h-5 w-2.5 -translate-y-1/2 rounded-l-[4px] bg-ink-950" />
    </span>
  ),

  spotlight: (
    <span className="relative block h-full">
      <Page />
      <span className="absolute bottom-0 left-0 flex w-[58%] items-center gap-1.5 rounded-[6px] bg-white p-1.5 shadow-[0_2px_4px_rgba(10,10,11,0.06),0_10px_20px_-8px_rgba(20,40,120,0.4)]">
        <span className="size-4 shrink-0 rounded-[4px] bg-[#f2c7a5]" />
        <span className="flex flex-1 flex-col gap-[3px]">
          <Stars small />
          <Line />
        </span>
      </span>
    </span>
  ),
};
